import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';
import { MediaManagerFileSystem } from '../../../models/media-manager/media-manager-file-system';
import { MediaManagerUtils } from '../../../utils/media-manager-utils';
import { findFolderInLibrary } from '../mutations/asset-library-lookups';
import {
  getProtectedSpecialAncestorForFolder,
  getProtectedTopLevelSpecialFolderForFolderId,
  getReservedFolderNameConflict,
  getReservedVirtualSectionName,
  getSpecialFolderPolicyByFolderId,
  getSpecialFolderPolicyByFolderName
} from '../policy/special-folder-policy';

/**
 * Verify a folder update against the current filesystem and asset library. Returns `null` when valid,
 * otherwise a `.`-joined error message spanning parent-change, rename, and lock-self checks.
 */
export function preflightFolderUpdateValidation(
  req: MediaManagerFolderDto,
  currentFolder: MediaManagerFolderDto,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): string | null {
  const errors: string[] = [];
  const parentChanged = req.parentFolderId !== currentFolder.parentFolderId;
  const folderNameChanged = req.name !== currentFolder.name;
  const specialFolderPolicy = getSpecialFolderPolicyByFolderId(currentFolder.id, filesystem);
  if (parentChanged) {
    const parentChangeErrors = preflightFolderParentChangeErrors(req, currentFolder, filesystem, assetLibrary);
    if (parentChangeErrors?.length) errors.push(...parentChangeErrors);
  }
  if (folderNameChanged) {
    const metadataChangeErrors = preflightFolderMetadataChangeErrors(req, currentFolder, assetLibrary, filesystem);
    if (metadataChangeErrors?.length) errors.push(...metadataChangeErrors);
  }
  if ((parentChanged || folderNameChanged) && specialFolderPolicy?.lockSelf) {
    errors.push(`Folder "${specialFolderPolicy.displayName}" is protected and cannot be renamed or moved`);
  }
  return errors.length ? errors.join('. ') : null;
}

/**
 * Parent-folder change constraints: existence checks, cycle-protection (folder cannot be moved under itself or
 * one of its own descendants), and protected-ancestor policy.
 */
export function preflightFolderParentChangeErrors(
  req: MediaManagerFolderDto,
  currentFolder: MediaManagerFolderDto,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): string[] {
  const errors: string[] = [];
  const currentFolderId = currentFolder.id;
  const currentParentId = currentFolder.parentFolderId;
  const targetParentId = req.parentFolderId;
  if (!targetParentId?.trim()) {
    errors.push('Updated parent folder ID is not valid');
    return errors;
  }
  if (!filesystem.folderExists(currentFolderId)) {
    errors.push(`Current folder with id '${currentFolderId}' does not exist in the filesystem`);
  }
  if (!filesystem.folderExists(targetParentId)) {
    errors.push(`Target parent folder with id '${targetParentId}' does not exist in the filesystem`);
  }
  if (currentParentId === targetParentId) {
    errors.push(`Folder with id '${currentFolderId}' is already in folder with id '${targetParentId}'`);
  }
  if (currentFolderId === targetParentId) {
    errors.push(`Cannot move folder '${currentFolderId}' into itself`);
  }
  if (filesystem.isFolderWithinFolder(targetParentId, currentFolderId)) {
    errors.push(`Cannot move folder '${currentFolderId}' into its own descendant folder '${targetParentId}'`);
  }
  const protectedAncestorFolder = getProtectedSpecialAncestorForFolder(currentFolder, filesystem, assetLibrary);
  if (protectedAncestorFolder) {
    errors.push(
      `Cannot move folder '${currentFolderId}' because it is inside protected folder "${protectedAncestorFolder.name}"`
    );
  }
  return errors;
}

/**
 * Verify a folder rename. Rejects blank names, reserved names (POS Media, POS Assets), and names that would
 * collide with an active reserved name owned by a different folder.
 */
export function preflightFolderMetadataChangeErrors(
  req: MediaManagerFolderDto,
  currentFolder: MediaManagerFolderDto | undefined,
  assetLibrary: MediaManagerAssetLibraryDto,
  filesystem: MediaManagerFileSystem
): string[] {
  const errors: string[] = [];
  if (!req.name?.trim()) {
    errors.push('Display name cannot be whitespace only');
  }
  const reservedVirtualSectionName = getReservedVirtualSectionName(req.name);
  if (reservedVirtualSectionName) {
    errors.push(`Folder name "${reservedVirtualSectionName}" is reserved and cannot be assigned manually`);
  }
  const reservedFolderPolicy = getSpecialFolderPolicyByFolderName(req.name, filesystem);
  if (reservedFolderPolicy) {
    errors.push(`Folder name "${reservedFolderPolicy.displayName}" is reserved and cannot be assigned manually`);
  } else {
    const reservedFolderNameConflict = getReservedFolderNameConflict(
      req.name,
      currentFolder?.id,
      assetLibrary,
      filesystem
    );
    if (reservedFolderNameConflict) {
      errors.push(`Folder name "${reservedFolderNameConflict.displayName}" is reserved and must be globally unique`);
    }
  }
  return errors;
}

/**
 * Verify a folder create request: parent-folder existence, structural protection on the parent, blank/reserved
 * name checks, and id-uniqueness inside the asset library.
 */
export function preflightCreateFolderValidation(
  req: MediaManagerFolderDto,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): string | null {
  const errors: string[] = [];
  if (!req.parentFolderId?.trim()) {
    errors.push('Parent folder ID is not valid');
  } else if (!filesystem.folderExists(req.parentFolderId)) {
    errors.push(`Parent folder '${req.parentFolderId}' does not exist in the filesystem`);
  } else {
    const parentFolderDepth = filesystem.getFolderDepth(req.parentFolderId);
    if (MediaManagerUtils.folderCreationDepthLimitReached(parentFolderDepth)) {
      errors.push(
        `Cannot create a folder inside folder with id ` +
          `'${req.parentFolderId}' because folders cannot exceed depth 2`
      );
    }
  }
  const protectedTopLevelFolder = getProtectedTopLevelSpecialFolderForFolderId(
    req.parentFolderId,
    filesystem,
    assetLibrary
  );
  if (protectedTopLevelFolder) {
    errors.push(`Cannot manually create folders inside protected folder "${protectedTopLevelFolder.name}"`);
  }
  if (req.name === null || !req.name?.trim()) {
    errors.push('Display name cannot be whitespace only');
  }
  const reservedVirtualSectionName = getReservedVirtualSectionName(req.name);
  if (reservedVirtualSectionName) {
    errors.push(`Folder name "${reservedVirtualSectionName}" is reserved and cannot be created manually`);
  }
  const reservedFolderPolicy = getSpecialFolderPolicyByFolderName(req.name, filesystem);
  if (reservedFolderPolicy) {
    errors.push(`Folder name "${reservedFolderPolicy.displayName}" is reserved and cannot be created manually`);
  } else {
    const reservedFolderNameConflict = getReservedFolderNameConflict(req.name, req.id, assetLibrary, filesystem);
    if (reservedFolderNameConflict) {
      errors.push(`Folder name "${reservedFolderNameConflict.displayName}" is reserved and must be globally unique`);
    }
  }
  if (req.id?.trim() && findFolderInLibrary(req.id, assetLibrary)) {
    errors.push(`MediaManager folder '${req.id}' already exists in assetLibrary`);
  }
  return errors.length ? errors.join('. ') : null;
}

/**
 * Verify a folder delete. Supports both delete-all and move-contents-to-target flows: the target folder must
 * exist, cannot be the folder being deleted, and cannot be one of its descendants.
 */
export function preflightDeleteFolderValidation(
  id: string,
  targetFolderId: string,
  deleteAllItems: boolean,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): string | null {
  const errors: string[] = [];
  const canonicalFolder = findFolderInLibrary(id, assetLibrary);
  if (!id?.trim()) {
    errors.push('Folder ID is not valid');
    return errors.join('. ');
  }
  if (id === 'ROOT') {
    errors.push(`Cannot delete folder '${id}'`);
  }
  if (!canonicalFolder) {
    errors.push(`MediaManager folder '${id}' could not be found in assetLibrary`);
  }
  if (!filesystem.folderExists(id)) {
    errors.push(`Folder '${id}' does not exist in the filesystem`);
  }
  if (!deleteAllItems) {
    if (!targetFolderId?.trim()) {
      errors.push('Target folder ID is not valid');
    } else {
      if (!filesystem.folderExists(targetFolderId)) {
        errors.push(`Target folder '${targetFolderId}' does not exist in the filesystem`);
      }
      if (targetFolderId === id) {
        errors.push(`Cannot move folder '${id}' into itself`);
      }
      if (filesystem.isFolderWithinFolder(targetFolderId, id)) {
        errors.push(`Cannot move contents of folder '${id}' into its own descendant folder '${targetFolderId}'`);
      }
    }
  }
  const specialFolderPolicy = canonicalFolder ? getSpecialFolderPolicyByFolderId(canonicalFolder.id, filesystem) : null;
  if (specialFolderPolicy?.lockSelf) {
    errors.push(`Folder "${specialFolderPolicy.displayName}" is protected and cannot be deleted`);
  }
  const protectedAncestorFolder = canonicalFolder
    ? getProtectedSpecialAncestorForFolder(canonicalFolder, filesystem, assetLibrary)
    : null;
  if (protectedAncestorFolder) {
    errors.push(`Cannot delete folder '${id}' because it is inside protected folder "${protectedAncestorFolder.name}"`);
  }
  return errors.length ? errors.join('. ') : null;
}
